import { useCallback, useEffect, useState } from 'react';
import Button from 'devextreme-react/button';
import TextBox from 'devextreme-react/text-box';
import LoadIndicator from 'devextreme-react/load-indicator';
import Popup from 'devextreme-react/popup';
import { shouldTreatUnlockDialogHidingAsCancel } from './jencUnlockState';
import './jencUnlockDialog.css';

export interface JencUnlockDialogProps {
  visible: boolean;
  fileName: string;
  /** New blank .jenc files need a passphrase before the first save. */
  mode?: 'unlock' | 'set-passphrase';
  managedRecoveryAvailable: boolean;
  hasManagedWrappedKey: boolean;
  onUnlock: (passphrase: string, useManagedRecovery: boolean) => Promise<void>;
  onCancel: () => void;
}

export function JencUnlockDialog({
  visible,
  fileName,
  mode = 'unlock',
  managedRecoveryAvailable,
  hasManagedWrappedKey,
  onUnlock,
  onCancel
}: JencUnlockDialogProps) {
  const [passphrase, setPassphrase] = useState('');
  const [useManagedRecovery, setUseManagedRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initializing = mode === 'set-passphrase';
  const canUseManagedRecovery = !initializing && managedRecoveryAvailable && hasManagedWrappedKey;

  useEffect(() => {
    if (!visible) {
      setPassphrase('');
      setUseManagedRecovery(false);
      setBusy(false);
      setError(null);
    }
  }, [visible]);

  const submit = useCallback(async () => {
    if (!useManagedRecovery && !passphrase.trim()) {
      setError(initializing ? 'Enter a passphrase to protect this file.' : 'Enter a passphrase.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await onUnlock(passphrase, useManagedRecovery);
    } catch (unlockError) {
      const message =
        unlockError instanceof Error ? unlockError.message : 'Could not unlock encrypted file.';
      setError(message);
    } finally {
      setBusy(false);
    }
  }, [initializing, onUnlock, passphrase, useManagedRecovery]);

  return (
    <Popup
      visible={visible}
      onHiding={() => {
        if (shouldTreatUnlockDialogHidingAsCancel(visible)) onCancel();
      }}
      showTitle
      showCloseButton
      title={initializing ? 'Protect encrypted file' : 'Unlock encrypted file'}
      width={420}
      height="auto"
      dragEnabled={false}
      hideOnOutsideClick={false}
      wrapperAttr={{ class: 'jenc-unlock-dialog-popup' }}
    >
      <div className="jenc-unlock-dialog">
        <p className="jenc-unlock-dialog__lead">
          {initializing ? (
            <>
              <strong>{fileName}</strong> is a new encrypted file. Choose a passphrase to protect
              it. You will need this passphrase to open the file later.
            </>
          ) : (
            <>
              <strong>{fileName}</strong> is encrypted. Enter the passphrase used when the file was
              saved.
            </>
          )}
        </p>
        {!useManagedRecovery ? (
          <TextBox
            mode="password"
            label="Passphrase"
            labelMode="floating"
            value={passphrase}
            onValueChanged={(event) => setPassphrase(String(event.value ?? ''))}
            onEnterKey={() => void submit()}
            disabled={busy}
          />
        ) : (
          <p className="jenc-unlock-dialog__hint">
            Managed recovery will use the server key to decrypt this file without a passphrase.
          </p>
        )}
        {canUseManagedRecovery ? (
          <label className="jenc-unlock-dialog__managed">
            <input
              type="checkbox"
              checked={useManagedRecovery}
              onChange={(event) => setUseManagedRecovery(event.target.checked)}
              disabled={busy}
            />
            Use managed recovery (no passphrase)
          </label>
        ) : null}
        {error ? (
          <p className="jenc-unlock-dialog__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="jenc-unlock-dialog__actions">
          <Button text="Cancel" stylingMode="outlined" disabled={busy} onClick={onCancel} />
          <Button
            text={initializing ? 'Set passphrase' : 'Unlock'}
            type="default"
            stylingMode="contained"
            disabled={busy}
            onClick={() => void submit()}
          />
          {busy ? <LoadIndicator height={20} width={20} visible /> : null}
        </div>
      </div>
    </Popup>
  );
}
